import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { getEnvironmentAccountId } from "@/lib/accountContext";
import { apiFetch, resolveApiUrl } from "@/lib/api";
import { myAccountSettingsQueryKey } from "@/lib/authQueryCache";
import { type Role, SELECTABLE_ROLES } from "@/lib/permissions";
import { useUserStore } from "@/store/userStore";
import type { MyAccountSettingsOutput } from "@/types";

export function shouldSyncCurrentUserStore() {
  return !getEnvironmentAccountId();
}

export function getPreferredRoleFromSettings(data: MyAccountSettingsOutput): Role | null {
  if (!data.default_role) return null;

  const isSelectableRole = (SELECTABLE_ROLES as string[]).includes(data.default_role);
  const isAvailableRole = data.available_roles.some((role) => role.code === data.default_role);

  return isSelectableRole && isAvailableRole ? (data.default_role as Role) : null;
}

export function syncCurrentUserFromAccountSettings(
  setAuthenticatedAccountId: (accountId: string | null) => void,
  setUser: (user: Partial<ReturnType<typeof useUserStore.getState>["user"]>) => void,
  data: MyAccountSettingsOutput
) {
  if (!shouldSyncCurrentUserStore()) {
    return;
  }

  setAuthenticatedAccountId(data.account_id);
  setUser({
    account_id: data.account_id,
    name: data.account_name,
    email: data.email,
    avatarUrl: resolveApiUrl(data.avatar_url),
    language: data.language,
  });
}

export function useMyAccountSettings() {
  const setAuthenticatedAccountId = useUserStore((state) => state.setAuthenticatedAccountId);
  const setUser = useUserStore((state) => state.setUser);
  const setPreferredRole = useUserStore((state) => state.setPreferredRole);
  const authTransition = useUserStore((state) => state.authTransition);

  const result = useQuery({
    queryKey: myAccountSettingsQueryKey,
    queryFn: () => apiFetch<MyAccountSettingsOutput>("/v1/account-settings"),
    enabled: authTransition !== "logout",
  });

  useEffect(() => {
    if (result.data) {
      syncCurrentUserFromAccountSettings(setAuthenticatedAccountId, setUser, result.data);
      setPreferredRole(getPreferredRoleFromSettings(result.data));
    }
  }, [result.data, setAuthenticatedAccountId, setUser, setPreferredRole]);

  return result;
}
